import { FC, useMemo } from 'react';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import { formatNumber } from 'utils/number';
import moment from 'moment';
import type { StripPriceData } from './types';
import style from './price-schedule-table.module.scss';

interface PriceScheduleRow extends Record<string, unknown> {
  id: string;
  date: string;
  oilPrice: number | null;
  gasPrice: number | null;
  valOil: number | null;
  valGas: number | null;
}

interface PriceScheduleTableProps {
  data: StripPriceData;
}

const formatPrice = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  return formatNumber({
    value,
    style: 'currency',
    minPrecision: 2,
    maxPrecision: 3,
  });
};

const formatDate = (date: string): string =>
  moment.utc(date, 'YYYY-MM-DD').format('MMM YY');

export const PriceScheduleTable: FC<PriceScheduleTableProps> = ({ data }) => {
  const rows: PriceScheduleRow[] = useMemo(
    () =>
      data.pricingDates.map((date, i) => ({
        id: date,
        date,
        oilPrice: data.oilPrices[i] !== null ? Number(data.oilPrices[i]) : null,
        gasPrice: data.gasPrices[i] !== null ? Number(data.gasPrices[i]) : null,
        valOil:
          data.valuationOilPrices[i] !== null
            ? Number(data.valuationOilPrices[i])
            : null,
        valGas:
          data.valuationGasPrices[i] !== null
            ? Number(data.valuationGasPrices[i])
            : null,
      })),
    [data]
  );

  const columns: DataTableColumnDef<PriceScheduleRow>[] = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ getValue }) => formatDate(getValue() as string),
      },
      {
        accessorKey: 'oilPrice',
        header: 'Oil Price',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
      {
        accessorKey: 'gasPrice',
        header: 'Gas Price',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
      {
        accessorKey: 'valOil',
        header: 'Val. Oil',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
      {
        accessorKey: 'valGas',
        header: 'Val. Gas',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
    ],
    []
  );

  return (
    <div className={style.tableWrapper}>
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        scroll={{ mode: 'paginated' }}
        config={{
          export: {
            exportPresets: [
              { name: 'Export', format: 'csv', filename: 'price-schedule' },
            ],
          },
          columnPinning: false,
          columnReordering: false,
          columnResizing: false,
        }}
        searchPlaceholder="Search..."
      />
    </div>
  );
};
